import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ComponentAdapterCounterDistributor } from "../../class/adapters/component-adapter-counter-distributor";
import { WidgetParent } from "../../class/widget-parent";
import { ComponentAdapterFactory } from "../../class/adapters/component-adapter-factory";
import { IWidgetExpandable } from "../../interfaces/IWidgetExpandable";
import { CounterHeaderData } from "../../class/counter-header-data";
import { DistributorCounterMeasurementType } from "../../enum/enumCounterMeasurementType";
import { DateHelper } from "../../class/general-classes/date-helper";
import { SmartPaletteCounter } from "../../class/smart-palette";
import { CounterHeaderComponent } from "../counter/counter-header/counter-header.component";
import { CounterHeatAllocatorComponent } from "../counter/counter-heat-allocator/counter-heat-allocator.component";
import { IonIcon } from "@ionic/angular";

@Component({
    selector: "app-widget-counter-distributor",
    templateUrl: "./widget-counter-distributor.component.html",
    styleUrls: [
        "./widget-counter-distributor.component.scss",
        "../../styles/shared-global.scss",
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CounterHeaderComponent, CounterHeatAllocatorComponent, IonIcon]
})
export class WidgetCounterDistributorComponent
  extends WidgetParent<ComponentAdapterCounterDistributor, SmartPaletteCounter>
  implements IWidgetExpandable
{
  private _currentMonthValue: string = "";
  private _previousMonthValue: string = "";

  public isExpanded: boolean = false;
  public areChartsVisible: boolean = false;
  public headerData: CounterHeaderData = null;

  /**
   * Open or close the widget
   */
  public onClickOpenCloseWidget(): void {
    if (!this.isExpanded) {
      this.onExpandWidget();
    } else {
      this.onCollapseWidget();
    }
  }

  onExpandWidget(): void {
    this.isExpanded = true;
  }

  onCollapseWidget(): void {
    this.isExpanded = false;
  }

  protected override onLanguageChange(): void {
    const now = new Date();
    this._currentMonthValue = this._localeService.formatDate(now, "month").toUpperCase();
    this._previousMonthValue = this._localeService.formatDate(DateHelper.subtract(now, 1, "month"), "month").toUpperCase();

    if (this.headerData) {
      this.headerData.subtitle = this._currentMonthValue;
      this.headerData.title1 = this._translateService.instant("dictionary.currentMonth");
      this.headerData.title2 = this._translateService.instant("dictionary.previousMonth");
    }

    if (this.componentAdapter) {
      this.componentAdapter.CurrentMonth = this._currentMonthValue;
      this.componentAdapter.PreviousMonth = this._previousMonthValue;
    }
  }

  protected setWidgetSmartPalette(): SmartPaletteCounter {
    // All counters share the same brand-derived palette (see setWidgetSmartPaletteCounterDefault).
    return this.setWidgetSmartPaletteCounterDefault();
  }

  protected onComponentLoad(): void {}

  protected onSmartComponentSuccessLoaded(): void {
    const factory = new ComponentAdapterFactory();
    this.loadSummaryData();
    this.componentAdapter = factory.createComponentAdapter(
      this._listSmartComponentSkills,
      ComponentAdapterCounterDistributor,
      this._headerSummary
    );

    this.componentAdapter.fillMeasurementMap(
      this._listSmartComponentCounterMeasurements,
      DistributorCounterMeasurementType,
      this.defaultDecimalDigits,
      this._localeService.locale()
    );

    this.prepareHeaderData();
  }

  protected onComponentDestroy(): void {}

  protected onSmartComponentErrorLoading(): void {}

  public onAddressValueChange(
    instanceGUID: string,
    address: string,
    value: string
  ): void {}

  /**
   * This method prepare and initialise the data passed to the header compoent. These data are:
   * + Taken from the previously fetched property.set of the SmartComponent (Name)
   * + Defined locally as localised text strings based on the counter sub-type (Titles)
   * + Calculated locally (Subtitle)
   */
  private prepareHeaderData(): void | boolean {
    const dataToHeader: CounterHeaderData = new CounterHeaderData();
    if (this.smartComponent.summary) {
      dataToHeader.titleWidget = this._translateService.instant("dictionary.consumptionDistribution");
      dataToHeader.subtitle = "";
    } else {
      dataToHeader.titleWidget = this.componentAdapter.Name;
      dataToHeader.subtitle = this._currentMonthValue;
    }

    //If component title is empty show error loading component
    if (!dataToHeader.titleWidget) {
      return (this._errorLoading = true);
    }

    if (this.componentAdapter.Consumption) {
      dataToHeader.title1 = this._translateService.instant("dictionary.currentMonth");
      dataToHeader.value1 =
        this.componentAdapter.MeasurementMap.get(
          DistributorCounterMeasurementType[
            DistributorCounterMeasurementType.DISTRIBUTOR_CONSUMPTION_LAST_MONTH
          ]
        ) || "---";

      dataToHeader.title2 = this._translateService.instant("dictionary.previousMonth");
      dataToHeader.value2 =
        this.componentAdapter.MeasurementMap.get(
          DistributorCounterMeasurementType[
            DistributorCounterMeasurementType
              .DISTRIBUTOR_CONSUMPTION_PREVIOUS_MONTH
          ]
        ) || "---";
    }

    this.headerData = dataToHeader;

    this.isExpanded = this._isExpanded;
    this.areChartsVisible = this._isExpanded ? true : false;
  }
}
